import { inject, Injectable } from '@angular/core';
import { marker } from '@common/translate-marker';
import { TranslateService } from '@ngx-translate/core';
import { FileRestrictions } from '@progress/kendo-angular-upload';
import { ToastService } from '@services/toast.service';

declare global {
    interface Navigator {
        msSaveBlob: (blob: Blob, defaultName?: string) => boolean;
    }
}

@Injectable({
    providedIn: 'root'
})
export class FileDownloadService {
    files: Array<any> = [];
    attachmentFileUploadRestrictions: FileRestrictions;

    private toastyService = inject(ToastService);
    private translateService = inject(TranslateService);

    constructor() {}

    public handleAttachmentDownload(data) {
        const arrayBuffer = this.base64ToArrayBuffer(data.base64Content);
        this.createAndDownloadBlobFile(arrayBuffer, data.name);
    }

    fileSelected($event) {
        if ($event.files.length > 10) {
            this.toastyService.error(this.translateService.instant(marker('Please select maximum of 10 files')));
            $event.preventDefault();
            return;
        }

        $event.files.forEach((element) => {
            if (element.size > this.attachmentFileUploadRestrictions.maxFileSize) {
                this.toastyService.error(
                    `${this.translateService.instant(marker('Attachment size invalid'))} - ${element.name}`
                );
                return;
            }

            if (!this.attachmentFileUploadRestrictions.allowedExtensions.some((ext) => ext == element.extension)) {
                this.toastyService.error(
                    `${this.translateService.instant(marker('Invalid attachment'))} - ${element.name}`
                );
                $event.preventDefault();
                return;
            }

            const raw = element.rawFile;

            if (raw) {
                const reader = new FileReader();
                reader.onload = (e) => {
                    this.documentOnLoad(e.target.result.toString(), raw.name);
                };
                reader.readAsDataURL(element.rawFile);
            }
        });
    }

    fileRemoved($event) {
        $event.files.forEach((element) => {
            const file = this.files.filter((x) => x.base64ContentName == element.name);
            if (file == null || file.length == 0) return;
            this.files.splice(this.files.indexOf(file[0]), 1);
        });
    }

    downloadAttachment(data: any) {
        if (data) {
            this.handleAttachmentDownload(data);
        }
    }

    documentOnLoad(file: string, name: string) {
        this.files.push({ base64Content: file, base64ContentName: name });
    }

    setAttachmentFileUploadRestrictions(value: FileRestrictions) {
        this.attachmentFileUploadRestrictions = value;
    }

    private base64ToArrayBuffer(base64: string) {
        const binaryString = window.atob(base64); // Comment this if not using base64
        const bytes = new Uint8Array(binaryString.length);
        return bytes.map((byte, i) => binaryString.charCodeAt(i));
    }

    private createAndDownloadBlobFile(body, filename) {
        const blob = new Blob([body]);
        const fileName = `${filename}`;
        if (navigator.msSaveBlob) {
            // IE 10+
            navigator.msSaveBlob(blob, fileName);
        } else {
            const link = document.createElement('a');
            // Browsers that support HTML5 download attribute
            if (link.download !== undefined) {
                const url = URL.createObjectURL(blob);
                link.setAttribute('href', url);
                link.setAttribute('download', fileName);
                link.style.visibility = 'hidden';
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
            }
        }
    }
}
